import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  useTheme
} from '@mui/material';

interface AddDocTypeDialogProps {
  open: boolean;
  loading: boolean;
  newDocTypeName: string;
  availableDocTypeOptions: string[];
  onDocTypeChange: (docType: string) => void;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

const AddDocTypeDialog: React.FC<AddDocTypeDialogProps> = ({
  open,
  loading,
  newDocTypeName,
  availableDocTypeOptions,
  onDocTypeChange,
  onConfirm,
  onCancel
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="new-doc-type-title"
      PaperProps={{
        sx: {
          borderRadius: 1,
          maxWidth: 'sm',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgb(30, 30, 30)' : 'background.paper',
        }
      }}
    >
      <DialogTitle id="new-doc-type-title">Add New Document Type</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Select a document type to add to this hotel's financial data.
        </DialogContentText>
        {availableDocTypeOptions.length > 0 ? (
          <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
            <InputLabel id="new-doc-type-select-label">Document Type</InputLabel>
            <Select
              labelId="new-doc-type-select-label"
              value={newDocTypeName}
              onChange={(e) => onDocTypeChange(e.target.value)}
              label="Document Type"
            >
              <MenuItem value="" disabled>
                <em>Select document type</em>
              </MenuItem>
              {availableDocTypeOptions.map((docType) => (
                <MenuItem key={docType} value={docType}>
                  {docType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Alert severity="info">
            All available document types have already been added to this hotel.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={!newDocTypeName || availableDocTypeOptions.length === 0 || loading}
        >
          {loading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDocTypeDialog;